import Button from "@clayui/button";
import DropDown from "@clayui/drop-down/lib/DropDown";
import ClayIcon from "@clayui/icon";
import { spritemap } from "@images/index";
import { useState, useEffect } from "react";
import styles from "./DropdownSelectBox.module.scss";
import { staticStrings } from "@staticStrings";
import { ContainerLoader } from "../container-loader/ContainerLoader";
import { Tooltip } from "react-tooltip";
import { ScoreInfoIcon as infoIcon } from "@images/index";
interface Option {
  label: string;
  info?: string;
}
interface DropdownSelectBoxProps {
  selectedOption: string;
  options: string[];
  setSelectedOption: (option: string) => void;
  dropdownId: string;
  placeHolder?: string;
  disableFlag?: boolean;
  loader?: boolean;
  searchOption?: boolean;
  dropDownLabel?: string;
  enableInfoText?: boolean;
  infoOptions?: Option[];
}

const DropdownSelectBox: React.FC<DropdownSelectBoxProps> = ({
  selectedOption,
  options,
  setSelectedOption,
  dropdownId,
  enableInfoText,
  infoOptions,
  placeHolder = staticStrings.commonTexts.select,
  disableFlag = false,
  loader = false,
  searchOption = false,
  dropDownLabel = ""
}) => {
  const [open, setOpen] = useState(false);
  const [searchInput, setSearchInput] = useState("");

  useEffect(() => {
    const id = `dropdown-overflow-${dropdownId}`;

    if (open && enableInfoText) {
      // Use requestAnimationFrame to ensure dropdown menu is rendered in DOM
      const frameId = requestAnimationFrame(() => {
        if (!document.getElementById(id)) {
          const style = document.createElement("style");
          style.id = id;
          style.innerHTML = `
          div div div[class="dropdown-menu show"] {
              overflow: visible !important;
            }
          `;
          document.head.appendChild(style);
        }
      });

      return () => {
        cancelAnimationFrame(frameId);
        const style = document.getElementById(id);
        if (style) {
          style.remove();
        }
      };
    } else {
      const style = document.getElementById(id);
      if (style) {
        style.remove();
      }
    }
  }, [open, dropdownId, enableInfoText]);

  const filteredInfoOptions =
    enableInfoText && infoOptions
      ? infoOptions?.filter((item) =>
          item.label.toLowerCase().includes(searchInput.toLowerCase())
        )
      : undefined;

  const filteredStringOptions = enableInfoText
    ? undefined
    : options?.filter((item) =>
        item.toLowerCase().includes(searchInput.toLowerCase())
      );

  const renderDropdownItems = () => {
    if (loader) {
      return (
        <div className={styles.containerLoader}>
          <ContainerLoader msgRequired={false} />
        </div>
      );
    }

    if (enableInfoText && filteredInfoOptions) {
      return filteredInfoOptions.map((item, index) => (
        <DropDown.Item
          id={`dropdown_list_${dropdownId}${index}`}
          key={item.label}
          active={item.label === selectedOption}
          onClick={() => {
            setSelectedOption(item.label);
            setOpen(!open);
          }}
        >
          <span className={styles.infoContainer}>
            {item.label}
            {item.info && (
              <div className={styles.tooltipContainer}>
                <img
                  src={infoIcon}
                  alt="info"
                  data-tooltip-id={`tooltip-info-icon-${dropdownId}-${index}`}
                  className={styles.tooltipTrigger}
                />
                <Tooltip
                  id={`tooltip-info-icon-${dropdownId}-${index}`}
                  place="bottom"
                  positionStrategy="fixed"
                  offset={10}
                  className={`${styles.tooltip} increase-z-index`}
                >
                  <div dangerouslySetInnerHTML={{ __html: item.info }} />
                </Tooltip>
              </div>
            )}
          </span>
        </DropDown.Item>
      ));
    }

    if (filteredStringOptions) {
      return filteredStringOptions.map((item, index) => (
        <DropDown.Item
          id={`dropdown_list_${dropdownId}${index}`}
          key={item}
          active={item === selectedOption}
          onClick={() => {
            setSelectedOption(item);
            setOpen(!open);
          }}
        >
          {item}
        </DropDown.Item>
      ));
    }

    return null;
  };

  return (
    <DropDown
      active={open}
      className={styles.dropDown}
      filterKey=""
      onActiveChange={(value: boolean) => {
        setOpen(value);
        setSearchInput("");
      }}
      trigger={
        <Button
          className={styles.dropDownBtn}
          displayType="unstyled"
          onClick={() => setOpen(!open)}
          id={dropdownId}
          disabled={disableFlag}
        >
          <div className={styles.dropDownText}>
            {selectedOption !== ""
              ? dropDownLabel || selectedOption
              : placeHolder}
          </div>
          {!open ? (
            <ClayIcon
              symbol="angle-down"
              spritemap={spritemap}
              className={styles.arrowDown}
            />
          ) : (
            <ClayIcon
              symbol="angle-up"
              spritemap={spritemap}
              className={styles.arrowUp}
            />
          )}
        </Button>
      }
    >
      {searchOption ? (
        <DropDown.Search
          placeholder={staticStrings.account360.engagedPeople.search}
          className={`${styles.searchBox}`}
          id={`search_box_${dropdownId}`}
          value={searchInput}
          onChange={(val: string) => setSearchInput(val)}
        />
      ) : (
        <></>
      )}
      <DropDown.ItemList
        items={
          enableInfoText && filteredInfoOptions
            ? filteredInfoOptions
            : filteredStringOptions
        }
        className={styles.dropDownItem}
      >
        {renderDropdownItems()}
      </DropDown.ItemList>
      {(enableInfoText
        ? filteredInfoOptions?.length === 0
        : filteredStringOptions?.length === 0) && (
        <DropDown.Section className={`${styles.dropDownContainerWidth}`}>
          <p className={styles.textContent} style={{ padding: "0.5rem" }}>
            {staticStrings.commonTexts.noRecordsFound}
          </p>
        </DropDown.Section>
      )}
    </DropDown>
  );
};

export { DropdownSelectBox };
